import React, { useState } from 'react';
import Composer from '../components/Composer';
import Heatmap from '../components/Heatmap';
import Timeline from '../components/Timeline';
import { MOCK_ENTRIES } from '../data/mockData';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiArrowUpRight, FiFilter } = FiIcons;

const Dashboard = () => {
  const [entries, setEntries] = useState(MOCK_ENTRIES);

  const handleNewEntry = (newEntry) => {
    setEntries([newEntry, ...entries]);
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row h-full overflow-hidden">
      {/* Left Column: Composer & Scrollable Timeline */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 scrollbar-hide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <header className="flex justify-between items-end mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Deep Journal</h1>
              <p className="text-gray-500 text-sm">Welcome back, Alex. Your last sync was 2 hours ago.</p>
            </div>
            <div className="hidden md:flex gap-2">
               <button className="p-2 rounded-lg bg-ink-800 text-gray-400 hover:text-white border border-ink-700 transition-colors">
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              </button>
              <button className="p-2 rounded-lg bg-ink-800 text-gray-400 hover:text-white border border-ink-700 transition-colors">
                <SafeIcon icon={FiFilter} className="w-5 h-5" />
              </button>
            </div>
          </header>
          
          <Composer onSave={handleNewEntry} />
          
          <div className="lg:hidden mt-8 space-y-8">
            <Heatmap />
          </div>

          <Timeline entries={entries} />
        </motion.div>
      </div>

      {/* Right Column: Analytics & Context (Desktop only) */}
      <div className="hidden lg:block w-80 xl:w-96 border-l border-ink-700 p-8 overflow-y-auto bg-ink-900/50">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-8"
        >
          <Heatmap />
          
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-sm font-semibold text-gray-200">Current Trajectory</h3>
              <SafeIcon icon={FiArrowUpRight} className="w-4 h-4 text-aurora" />
            </div>
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-gray-400 uppercase tracking-wider font-mono">Resilience Index</span>
                  <span className="text-aurora font-bold">88%</span>
                </div>
                <div className="h-1.5 w-full bg-ink-700 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: '88%' }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    className="h-full bg-aurora rounded-full" 
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-gray-400 uppercase tracking-wider font-mono">Cognitive Load</span>
                  <span className="text-lilac font-bold">42%</span>
                </div>
                <div className="h-1.5 w-full bg-ink-700 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: '42%' }}
                    transition={{ duration: 1.5, delay: 0.7 }}
                    className="h-full bg-lilac rounded-full" 
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-ink-800/50 border border-ink-700">
               <p className="text-[11px] text-gray-400 leading-relaxed italic">
                "Your focus on #deepWork this morning has increased your projected flow state capacity by 14% for tomorrow."
              </p>
            </div>
          </div>

          <div className="glass-panel rounded-2xl p-6 border border-aurora/10">
            <h3 className="text-sm font-semibold text-gray-200 mb-4">Upcoming Nudge</h3>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-aurora/10 text-aurora">
                <SafeIcon icon={FiCalendar} className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-gray-200 font-medium">8:00 PM Reflection</p>
                <p className="text-[10px] text-gray-500 mt-1">Founders with consistent PM review report 22% less burnout risk.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;